import React, { useCallback, useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { useAuth } from '../contexts/AuthContext';
import {
    changePassword,
    setupTOTP,
    verifyTOTP,
    getMFAPreference,
    setMFAPreference,
    registerPasskey,
    listPasskeys,
    deletePasskey,
    getPhoneNumber,
    updatePhoneNumber,
    confirmPhoneNumber,
} from '../services/auth';
import './SettingsModal.css';

type Tab = 'account' | 'security' | 'passkeys';

interface Props {
    open: boolean;
    onClose: () => void;
}

interface WebAuthnCredential {
    credentialId: string;
    friendlyCredentialName: string;
    relyingPartyId: string;
    createdAt: Date;
}

export const SettingsModal: React.FC<Props> = ({ open, onClose }) => {
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState<Tab>('account');

    const handleKeyDown = useCallback((e: KeyboardEvent) => {
        if (e.key === 'Escape') onClose();
    }, [onClose]);

    useEffect(() => {
        if (!open) return;
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [open, handleKeyDown]);

    if (!open) return null;

    return (
        <div className="settings-overlay" onClick={onClose}>
            <div className="settings-modal" role="dialog" aria-modal="true" aria-labelledby="settings-title" onClick={(e) => e.stopPropagation()}>
                <div className="settings-header">
                    <h2 id="settings-title">Settings</h2>
                    <button className="settings-close" onClick={onClose} aria-label="Close settings">&times;</button>
                </div>
                <div className="settings-tabs">
                    <button className={`settings-tab ${activeTab === 'account' ? 'active' : ''}`} onClick={() => setActiveTab('account')}>Account</button>
                    <button className={`settings-tab ${activeTab === 'security' ? 'active' : ''}`} onClick={() => setActiveTab('security')}>Security</button>
                    <button className={`settings-tab ${activeTab === 'passkeys' ? 'active' : ''}`} onClick={() => setActiveTab('passkeys')}>Passkeys</button>
                </div>
                <div className="settings-body">
                    {activeTab === 'account' && <AccountTab email={user?.email ?? ''} />}
                    {activeTab === 'security' && <SecurityTab email={user?.email ?? ''} />}
                    {activeTab === 'passkeys' && <PasskeysTab />}
                </div>
            </div>
        </div>
    );
};

const AccountTab: React.FC<{ email: string }> = ({ email }) => {
    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPwd, setConfirmPwd] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);

    const handleChangePassword = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (newPassword.length < 8) { setError('Password must be at least 8 characters'); return; }
        if (newPassword.length > 128) { setError('Password must be no more than 128 characters'); return; }
        if (!/[A-Z]/.test(newPassword)) { setError('Password must contain at least one uppercase letter'); return; }
        if (!/[a-z]/.test(newPassword)) { setError('Password must contain at least one lowercase letter'); return; }
        if (!/[0-9]/.test(newPassword)) { setError('Password must contain at least one number'); return; }
        if (!/[^A-Za-z0-9]/.test(newPassword)) { setError('Password must contain at least one special character'); return; }
        if (newPassword !== confirmPwd) { setError('Passwords do not match'); return; }

        setLoading(true);
        try {
            await changePassword(oldPassword, newPassword);
            setSuccess('Password changed successfully.');
            setOldPassword('');
            setNewPassword('');
            setConfirmPwd('');
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Failed to change password');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="settings-section">
            <div className="settings-field">
                <label>Email</label>
                <span className="settings-value">{email}</span>
            </div>
            <h3>Change Password</h3>
            <form onSubmit={handleChangePassword}>
                <div className="settings-field">
                    <label htmlFor="settings-old-pwd">Current Password</label>
                    <input id="settings-old-pwd" type="password" value={oldPassword} onChange={(e) => setOldPassword(e.target.value)} required disabled={loading} />
                </div>
                <div className="settings-field">
                    <label htmlFor="settings-new-pwd">New Password</label>
                    <input id="settings-new-pwd" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required disabled={loading} />
                </div>
                <div className="settings-field">
                    <label htmlFor="settings-confirm-pwd">Confirm New Password</label>
                    <input id="settings-confirm-pwd" type="password" value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)} required disabled={loading} />
                </div>
                {error && <div className="settings-error">{error}</div>}
                {success && <div className="settings-success">{success}</div>}
                <button type="submit" disabled={loading} className="settings-btn">
                    {loading ? 'Changing...' : 'Change Password'}
                </button>
            </form>
        </div>
    );
};

const SecurityTab: React.FC<{ email: string }> = ({ email }) => {
    const [mfaPreference, setMfaPreferenceState] = useState<string>('loading');
    const [smsAvailable, setSmsAvailable] = useState(false);
    const [showTotpSetup, setShowTotpSetup] = useState(false);
    const [qrCodeUrl, setQrCodeUrl] = useState('');
    const [totpSecret, setTotpSecretState] = useState('');
    const [verifyCode, setVerifyCode] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    const [currentPhone, setCurrentPhone] = useState<string | null>(null);
    const [showPhoneSetup, setShowPhoneSetup] = useState(false);
    const [phoneInput, setPhoneInput] = useState('');
    const [phoneVerifyCode, setPhoneVerifyCode] = useState('');
    const [phoneStep, setPhoneStep] = useState<'input' | 'verify'>('input');

    useEffect(() => {
        loadMfaPreference();
        loadPhoneNumber();
    }, []);

    const loadMfaPreference = async () => {
        try {
            const pref = await getMFAPreference();
            const hasSms = pref.preferred === 'SMS' || !!pref.enabled?.includes('SMS');
            setSmsAvailable(hasSms);
            if (pref.preferred === 'TOTP') setMfaPreferenceState('totp');
            else if (pref.preferred === 'SMS') setMfaPreferenceState('sms');
            else if (pref.enabled?.includes('TOTP')) setMfaPreferenceState('totp');
            else if (pref.enabled?.includes('SMS')) setMfaPreferenceState('sms');
            else setMfaPreferenceState('none');
        } catch {
            setMfaPreferenceState('unknown');
        }
    };

    const loadPhoneNumber = async () => {
        const phone = await getPhoneNumber();
        setCurrentPhone(phone);
        if (phone) setPhoneInput(phone);
    };

    const handleUpdatePhone = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        const digits = phoneInput.replace(/\D/g, '');
        const normalized = phoneInput.startsWith('+') ? phoneInput : `+1${digits}`;

        if (!/^\+1\d{10}$/.test(normalized)) {
            setError('Enter a valid 10-digit US phone number (e.g., 212-555-1234)');
            return;
        }

        setPhoneInput(normalized);
        setLoading(true);
        try {
            await updatePhoneNumber(normalized);
            setPhoneStep('verify');
            setSuccess('A verification code has been sent to your phone number.');
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Failed to update phone number');
        } finally {
            setLoading(false);
        }
    };

    const handleConfirmPhone = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setLoading(true);
        try {
            await confirmPhoneNumber(phoneVerifyCode);
            setCurrentPhone(phoneInput);
            setShowPhoneSetup(false);
            setPhoneStep('input');
            setPhoneVerifyCode('');
            setSmsAvailable(true);
            setSuccess('Phone number verified. You can now use SMS for two-factor authentication.');
            await loadMfaPreference();
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Invalid verification code');
        } finally {
            setLoading(false);
        }
    };

    const handleSwitchMfa = async (method: 'totp' | 'sms') => {
        setError('');
        setSuccess('');
        setLoading(true);
        try {
            await setMFAPreference(method);
            setMfaPreferenceState(method);
            setSuccess(`Preferred MFA method set to ${method === 'totp' ? 'Authenticator App' : 'SMS'}.`);
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : 'Failed to update MFA preference';
            if (msg.toLowerCase().includes('delivery config') || msg.toLowerCase().includes('sms_mfa')) {
                setError('SMS is not available. You need a verified phone number to use SMS verification. Phone numbers can only be added during initial account setup.');
            } else {
                setError(msg);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleStartTotpSetup = async () => {
        setError('');
        setSuccess('');
        setLoading(true);
        try {
            const result = await setupTOTP();
            const uri = result.getSetupUri('SCAIT', email).toString();
            setTotpSecretState(result.sharedSecret);
            try {
                const url = await QRCode.toDataURL(uri, { width: 200 });
                setQrCodeUrl(url);
            } catch {
                setQrCodeUrl('');
            }
            setShowTotpSetup(true);
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Failed to start TOTP setup');
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyTotp = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            await verifyTOTP(verifyCode);
            setSuccess('Authenticator app configured successfully.');
            setShowTotpSetup(false);
            setVerifyCode('');
            setMfaPreferenceState('totp');
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Invalid code');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="settings-section">
            <h3>Two-Factor Authentication</h3>
            <div className="settings-field">
                <label>Current Method</label>
                <span className="settings-value mfa-status">
                    {mfaPreference === 'totp' && 'Authenticator App'}
                    {mfaPreference === 'sms' && 'SMS Text Message'}
                    {mfaPreference === 'none' && 'Not configured'}
                    {mfaPreference === 'loading' && 'Loading...'}
                    {mfaPreference === 'unknown' && 'Unable to load'}
                </span>
            </div>

            {!showTotpSetup && mfaPreference === 'none' && (
                <div className="mfa-actions">
                    <button onClick={handleStartTotpSetup} disabled={loading} className="settings-btn">
                        Set Up Authenticator App
                    </button>
                </div>
            )}

            {!showTotpSetup && mfaPreference !== 'none' && mfaPreference !== 'loading' && mfaPreference !== 'unknown' && (
                <div className="mfa-actions">
                    <button onClick={() => handleSwitchMfa('totp')} disabled={loading || mfaPreference === 'totp'} className="settings-btn-secondary">
                        {mfaPreference === 'totp' ? 'Authenticator (Active)' : 'Use Authenticator App'}
                    </button>
                    {smsAvailable && (
                        <button onClick={() => handleSwitchMfa('sms')} disabled={loading || mfaPreference === 'sms'} className="settings-btn-secondary">
                            {mfaPreference === 'sms' ? 'SMS (Active)' : 'Use SMS'}
                        </button>
                    )}
                    <button onClick={handleStartTotpSetup} disabled={loading} className="settings-btn">
                        Re-configure Authenticator App
                    </button>
                </div>
            )}

            {showTotpSetup && (
                <div className="totp-setup-inline">
                    {qrCodeUrl ? (
                        <div className="qr-container"><img src={qrCodeUrl} alt="TOTP QR Code" className="qr-code" /></div>
                    ) : totpSecret ? (
                        <div className="settings-error">QR code generation failed. Use the manual entry key below.</div>
                    ) : null}
                    {totpSecret && (
                        <div className="secret-key">
                            <span className="secret-label">Manual entry key:</span>
                            <code className="secret-value">{totpSecret}</code>
                        </div>
                    )}
                    <form onSubmit={handleVerifyTotp}>
                        <div className="settings-field">
                            <label htmlFor="settings-totp-code">Verification Code</label>
                            <input id="settings-totp-code" type="text" inputMode="numeric" pattern="[0-9]{6}" maxLength={6} value={verifyCode} onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ''))} required disabled={loading} placeholder="000000" autoComplete="one-time-code" />
                        </div>
                        <div className="mfa-actions">
                            <button type="submit" disabled={loading || verifyCode.length !== 6} className="settings-btn">
                                {loading ? 'Verifying...' : 'Verify & Activate'}
                            </button>
                            <button type="button" onClick={() => setShowTotpSetup(false)} className="settings-btn-secondary">Cancel</button>
                        </div>
                    </form>
                </div>
            )}

            <h3 style={{ marginTop: '24px' }}>Phone Number</h3>
            <div className="settings-field">
                <label>Current Phone</label>
                <span className="settings-value">{currentPhone || 'Not set'}</span>
            </div>

            {!showPhoneSetup && (
                <div className="mfa-actions">
                    <button onClick={() => { setShowPhoneSetup(true); setPhoneStep('input'); setError(''); setSuccess(''); }} disabled={loading} className="settings-btn">
                        {currentPhone ? 'Change Phone Number' : 'Add Phone Number'}
                    </button>
                </div>
            )}

            {showPhoneSetup && phoneStep === 'input' && (
                <form onSubmit={handleUpdatePhone}>
                    <div className="settings-field">
                        <label htmlFor="settings-phone">Phone Number</label>
                        <input id="settings-phone" type="tel" value={phoneInput} onChange={(e) => setPhoneInput(e.target.value)} required disabled={loading} placeholder="(212) 555-1234" />
                    </div>
                    <div className="mfa-actions">
                        <button type="submit" disabled={loading} className="settings-btn">
                            {loading ? 'Sending code...' : 'Send Verification Code'}
                        </button>
                        <button type="button" onClick={() => { setShowPhoneSetup(false); setPhoneInput(currentPhone || ''); }} className="settings-btn-secondary">Cancel</button>
                    </div>
                </form>
            )}

            {showPhoneSetup && phoneStep === 'verify' && (
                <form onSubmit={handleConfirmPhone}>
                    <div className="settings-field">
                        <label htmlFor="settings-phone-code">Verification Code</label>
                        <input id="settings-phone-code" type="text" inputMode="numeric" pattern="[0-9]{6}" maxLength={6} value={phoneVerifyCode} onChange={(e) => setPhoneVerifyCode(e.target.value.replace(/\D/g, ''))} required disabled={loading} placeholder="000000" autoComplete="one-time-code" />
                    </div>
                    <div className="mfa-actions">
                        <button type="submit" disabled={loading || phoneVerifyCode.length !== 6} className="settings-btn">
                            {loading ? 'Verifying...' : 'Verify Phone Number'}
                        </button>
                        <button type="button" onClick={() => { setShowPhoneSetup(false); setPhoneStep('input'); setPhoneVerifyCode(''); }} className="settings-btn-secondary">Cancel</button>
                    </div>
                </form>
            )}

            {error && <div className="settings-error">{error}</div>}
            {success && <div className="settings-success">{success}</div>}
        </div>
    );
};

const PasskeysTab: React.FC = () => {
    const [credentials, setCredentials] = useState<WebAuthnCredential[]>([]);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    const [loadingList, setLoadingList] = useState(true);

    useEffect(() => {
        loadCredentials();
    }, []);

    const loadCredentials = async () => {
        try {
            const result = await listPasskeys();
            setCredentials(result.credentials as WebAuthnCredential[]);
        } catch {
            setCredentials([]);
        } finally {
            setLoadingList(false);
        }
    };

    const handleRegister = async () => {
        setError('');
        setSuccess('');
        setLoading(true);
        try {
            await registerPasskey();
            setSuccess('Passkey registered successfully.');
            await loadCredentials();
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Failed to register passkey');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (credentialId: string) => {
        setError('');
        setSuccess('');
        setLoading(true);
        try {
            await deletePasskey(credentialId);
            setSuccess('Passkey removed.');
            await loadCredentials();
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Failed to remove passkey');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="settings-section">
            <h3>Passkeys</h3>
            <p className="settings-description">Passkeys let you sign in without a password using your device's biometrics or security key.</p>

            {loadingList ? (
                <p className="settings-loading">Loading passkeys...</p>
            ) : credentials.length === 0 ? (
                <p className="settings-empty">No passkeys registered.</p>
            ) : (
                <div className="passkey-list">
                    {credentials.map((cred) => (
                        <div key={cred.credentialId} className="passkey-item">
                            <div className="passkey-info">
                                <span className="passkey-name">{cred.friendlyCredentialName || 'Passkey'}</span>
                                <span className="passkey-date">
                                    {cred.createdAt ? new Date(cred.createdAt).toLocaleDateString() : ''}
                                </span>
                            </div>
                            <button onClick={() => handleDelete(cred.credentialId)} disabled={loading} className="passkey-delete">Remove</button>
                        </div>
                    ))}
                </div>
            )}

            <button onClick={handleRegister} disabled={loading} className="settings-btn" style={{ marginTop: '16px' }}>
                {loading ? 'Registering...' : 'Register New Passkey'}
            </button>

            {error && <div className="settings-error">{error}</div>}
            {success && <div className="settings-success">{success}</div>}
        </div>
    );
};
